/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable max-lines */
import type React from 'react';
import { useMemo, useCallback, useState, useContext } from 'react';
import { type TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import { ControlledDateTimePickerContext } from '../../../ControlledDateTimePickerCTX.js';
import type { useFocusLogic } from './useFocusLogic.js';

interface UsePopperTriggerLogic {
  focusLogic: ReturnType<typeof useFocusLogic>;
  showCalendar: boolean;
  setShowCalendar: TypescriptHelpersT.StateSetter<boolean>;
  resetMonthYear: () => void;
}
interface PopperTriggerLogicT {
  referenceElement: HTMLButtonElement | null;
  closeCalendar: () => void;
  handleToggleCalendar: () => void;
  handleSetTriggerRef: (instance: HTMLButtonElement) => void;
  handleMenuWrapperKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
}
export const usePopperTriggerLogic = ({
  focusLogic,
  showCalendar,
  setShowCalendar,
  resetMonthYear,
}: UsePopperTriggerLogic): PopperTriggerLogicT => {
  const { focusedDay, resetFocusedDayToStartDate } = focusLogic;
  const {
    props: { onPickerOpen, onPickerClose, disabled },

    pickerButtonRef,
    trackFocusCalendarMetafocusedDay,
    trackFocusCalendarPrevMonth,
    trackFocusPicker,
    latestInteractionRegion,
    currFocusDescriber,
  } = useContext(ControlledDateTimePickerContext);
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null);
  // onOpen we try to focus the day we should focus, if we should focus one
  const handleOnPickerOpen = useCallback(() => {
    onPickerOpen();
  }, [onPickerOpen]);
  const openCalendar = useCallback(() => {
    if (disabled) return;
    if (focusedDay) trackFocusCalendarMetafocusedDay();
    else setTimeout(() => trackFocusCalendarPrevMonth());
    setShowCalendar(true);
    handleOnPickerOpen();
  }, [
    disabled,
    focusedDay,
    handleOnPickerOpen,
    setShowCalendar,
    trackFocusCalendarMetafocusedDay,
    trackFocusCalendarPrevMonth,
  ]);

  const closeCalendar = useCallback(() => {
    setShowCalendar(false);
    onPickerClose();
    resetFocusedDayToStartDate();
    trackFocusPicker();
    resetMonthYear();
  }, [setShowCalendar, onPickerClose, resetFocusedDayToStartDate, trackFocusPicker, resetMonthYear]);

  const handleToggleCalendar = useCallback(() => {
    const isOpening = !showCalendar;
    if (isOpening) openCalendar();
    else closeCalendar();
  }, [closeCalendar, openCalendar, showCalendar]);

  const handleSetTriggerRef = useCallback(
    (newRef: HTMLButtonElement) => {
      pickerButtonRef.current = newRef;
      setReferenceElement(newRef);
      if (latestInteractionRegion === 'picker-icon' && currFocusDescriber === 'date-picker-icon') newRef?.focus();
    },
    [currFocusDescriber, latestInteractionRegion, pickerButtonRef],
  );
  const handleMenuWrapperKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      const { key } = e;
      if (key === 'Escape') {
        closeCalendar();
        trackFocusPicker();
      }
    },
    [closeCalendar, trackFocusPicker],
  );

  return useMemo(
    () => ({
      referenceElement,
      closeCalendar,
      handleSetTriggerRef,
      handleToggleCalendar,
      handleMenuWrapperKeyDown,
    }),
    [closeCalendar, handleMenuWrapperKeyDown, handleSetTriggerRef, handleToggleCalendar, referenceElement],
  );
};
